// features/Vault/vault.helpers.ts
import {
  type ClientSortKey,
} from 'pages/vault/components/VaultCardGrouped'

import { type MediaType } from './vault.constants'

export const variantKeyOf = (item: any) => String(item.__k ?? item.id ?? '')

export const postIdOf = (it: any): string | undefined =>
  it?.post?.id ??
  it?.post_id ??
  it?.story?.post_id ??
  it?.story?.post?.id

export const vaultIdOf = (it: any): string =>
  String(it?.parent_id ?? it?.id ?? '')

export const passesCategoryFilter = (
  item: any,
  categoryId: string | null,
  grouped: boolean
): boolean => {
  if (categoryId == null) return true
  const cid = categoryId

  if (grouped) {
    const postCats = item?.post?.media_categories
    const msgCats = item?.message?.message?.media_category
    const storyCats = item?.story?.media_categories

    const inPost =
      Array.isArray(postCats) && postCats.some((c: any) => c?.id === cid)

    const inMsg =
      Array.isArray(msgCats) && msgCats.some((c: any) => c?.id === cid)

    const inStory =
      Array.isArray(storyCats) && storyCats.some((c: any) => c?.id === cid)

    return inPost || inMsg || inStory
  }

  // UNGROUPED — POSTS
  if (item?.post) {
    const photos: any[] = item.post?.photos ?? []
    const videos: any[] = item.post?.videos ?? []
    const inPhotos = photos.some(
      p =>
        Array.isArray(p?.media_categories) &&
        p.media_categories.some((c: any) => c?.id === cid)
    )
    const inVideos = videos.some(
      v =>
        Array.isArray(v?.media_categories) &&
        v.media_categories.some((c: any) => c?.id === cid)
    )
    if (inPhotos || inVideos) return true
  }

  // UNGROUPED — MESSAGES
  if (item?.message?.message) {
    const msg = item.message.message
    const photos: any[] = msg?.photos ?? []
    const videos: any[] = msg?.videos ?? []
    const inPhotos = photos.some(
      p =>
        Array.isArray(p?.media_categories) &&
        p.media_categories.some((c: any) => c?.id === cid)
    )
    const inVideos = videos.some(
      v =>
        Array.isArray(v?.media_categories) &&
        v.media_categories.some((c: any) => c?.id === cid)
    )
    if (inPhotos || inVideos) return true
  }

  // UNGROUPED — STORIES
  if (item?.story) {
    const storyCats: any[] = item.story?.media_categories ?? []
    const inStory =
      Array.isArray(storyCats) && storyCats.some((c: any) => c?.id === cid)
    if (inStory) return true
  }

  return false
}

export const passesMediaTypeFilter = (item: any, mediaType: MediaType): boolean => {
  if (mediaType === 'all_media') return true

  // POSTS
  if (item?.post) {
    const hasPhotos = Array.isArray(item.post.photos) && item.post.photos.length > 0
    const hasVideos = Array.isArray(item.post.videos) && item.post.videos.length > 0
    return mediaType === 'photos' ? hasPhotos : hasVideos
  }

  // MESSAGES
  if (item?.message?.message) {
    const msg = item.message.message
    const hasPhotos = Array.isArray(msg.photos) && msg.photos.length > 0
    const hasVideos = Array.isArray(msg.videos) && msg.videos.length > 0
    return mediaType === 'photos' ? hasPhotos : hasVideos
  }

  // STORIES
  if (item?.story) {
    const hasPhoto = !!item.story.image_src
    const hasVideo = !!item.story.video_src
    return mediaType === 'photos' ? hasPhoto : hasVideo
  }

  return false
}

export const passesPaidOnlyFilter = (
  item: any,
  grouped: boolean,
  paidOnly: boolean
): boolean => {
  if (!paidOnly) return true

  if (grouped) {
    const postPaid = item?.post ? Number(item.post?.price ?? 0) > 0 : false
    const msgPaid = item?.message?.message ? Number(item.message.message?.price ?? 0) > 0 : false
    return postPaid || msgPaid
  }

  const v0 = item?.post?.videos?.[0] ?? item?.message?.message?.videos?.[0]
  const p0 = item?.post?.photos?.[0] ?? item?.message?.message?.photos?.[0]
  const m = v0 ?? p0
  return !!m && Number(m?.conversion_rate ?? 0) > 0
}

export const isZeroViews = (item: any, grouped: boolean): boolean => {
  // MESSAGE
  if (item?.message) {
    if (grouped) {
      const v = item.message?.statistics?.views
      return typeof v === 'number' ? v === 0 : false
    } else {
      const photos = item.message?.message?.photos ?? []
      const videos = item.message?.message?.videos ?? []

      const photosAllZero = photos.length === 0 || photos.every((p: any) => (p?.views ?? 0) === 0)
      const videosAllZero = videos.length === 0 || videos.every((v: any) => (v?.views ?? 0) === 0)

      const hasMedia = photos.length + videos.length > 0
      return hasMedia && photosAllZero && videosAllZero
    }
  }

  // POST
  if (item?.post) {
    if (grouped) {
      const v = item.post?.unique_views
      return typeof v === 'number' ? v === 0 : false
    } else {
      const photos = item.post?.photos ?? []
      const videos = item.post?.videos ?? []

      const getViews = (m: any) =>
        typeof m?.total_views === 'number'
          ? m.total_views
          : typeof m?.views === 'number'
            ? m.views
            : 0

      const photosAllZero = photos.length === 0 || photos.every((p: any) => getViews(p) === 0)
      const videosAllZero = videos.length === 0 || videos.every((v: any) => getViews(v) === 0)

      const hasMedia = photos.length + videos.length > 0
      return hasMedia && photosAllZero && videosAllZero
    }
  }

  return false
}

export const pickMetric = (it: any, grouped: boolean, key: ClientSortKey): number => {
  if (!key) return 0

  // GROUPED MESSAGES
  if (it?.message && grouped) {
    if (key === 'conversion_rate') return Number(it.message?.statistics?.conversion_rate ?? 0)
    if (key === 'views') return Number(it.message?.statistics?.views ?? 0)
    if (key === 'impressions') return Number(it.message?.statistics?.views ?? 0)
    if (key === 'purchases') return Number(it.message?.statistics?.purchase_count ?? 0)
  }

  // GROUPED POSTS
  if (it?.post && grouped) {
    if (key === 'conversion_rate') {
      const rate = it.post?.media_group_conversion_rate ?? it.post?.conversion_rate
      return Number(rate ?? 0)
    }
    if (key === 'views') return Number(it.post?.unique_views ?? 0)
    if (key === 'impressions') return Number(it.post?.unique_impressions ?? 0)
    if (key === 'purchases') return Number(it.post?.purchases_count ?? 0)
  }

  // UNGROUPED MESSAGES
  if (it?.message && !grouped) {
    const p0 = it.message?.message?.photos?.[0]
    const v0 = it.message?.message?.videos?.[0]
    const m = v0 ?? p0
    if (!m) return 0
    if (key === 'conversion_rate') return (m?.conversion_rate ?? Number.NEGATIVE_INFINITY) as number
    if (key === 'views') return Number(m?.views ?? 0)
    if (key === 'impressions') return Number(m?.views ?? 0)
    if (key === 'purchases') return Number(m?.purchases ?? 0)
  }

  // UNGROUPED POSTS
  if (it?.post && !grouped) {
    const p0 = it.post?.photos?.[0]
    const v0 = it.post?.videos?.[0]
    const m = v0 ?? p0
    if (!m) return 0
    if (key === 'conversion_rate') return (m?.conversion_rate ?? Number.NEGATIVE_INFINITY) as number
    if (key === 'views') return Number((m?.total_views ?? m?.views) ?? 0)
    if (key === 'impressions') return Number(it.post?.unique_impressions ?? 0)
    if (key === 'purchases') return Number(m?.purchases ?? 0)
  }

  return 0
}

export const passesTabFilterFactory = (activeFilter: string) => (item: any): boolean => {
  const isPost = !!item?.post
  const isMessage = !!item?.message?.message
  const isStoryObj = !!item?.story

  const hasLocked = (arr: any[]): boolean => Array.isArray(arr) && arr.some(m => m?.locked === true)

  const postPhotos = isPost ? (item.post?.photos ?? []) : []
  const postVideos = isPost ? (item.post?.videos ?? []) : []
  const postPrice = isPost ? Number(item.post?.price ?? 0) : 0
  const isPremiumPost = isPost && (postPrice > 0 || hasLocked(postPhotos) || hasLocked(postVideos))

  const postHasStoriesArray = isPost && Array.isArray(item.post?.stories) && item.post.stories.length > 0
  const postHasStoryFlag = isPost && (item.post?.story_post === true || item.post?.story === true)

  switch (activeFilter) {
    case 'feed':
      return isPost && !isPremiumPost
    case 'premium':
      return isPremiumPost
    case 'messages':
      return isMessage
    case 'story':
      return (isPost && (postHasStoriesArray || postHasStoryFlag)) || isStoryObj
    case 'all':
    default:
      return true
  }
}

export const coerceVaultCacheToArray = (cached: any): any[] => {
  if (Array.isArray(cached)) return cached
  if (Array.isArray(cached?.data)) return cached.data
  if (Array.isArray(cached?.pages)) return cached.pages.flatMap((p: any) => p?.data ?? [])
  return []
}
